import React, { useEffect, useRef } from "react";

/**
 * AdSlot (Google AdSense)
 * - Ne plante jamais l'app (try/catch)
 * - Attends que l'élément <ins> ait une largeur > 0 avant de push()
 * - Évite les double push sur le même slot
 *
 * Usage:
 * <AdSlot client="ca-pub-XXXX" slot="1234567890" />
 */
export default function AdSlot({
  client,
  slot,
  format = "auto",
  responsive = true,
  style = {},
  className = "",
  retryDelayMs = 200,
  maxRetries = 60, // ~12s si retryDelayMs=200
}) {
  const pushedRef = useRef(false);
  const retriesRef = useRef(0);
  const insRef = useRef(null);

  useEffect(() => {
    // Ne rien faire si pas configuré
    if (!client || !slot) return;

    let cancelled = false;

    const tryPush = () => {
      if (cancelled) return;

      // AdSense pas encore chargé (consentement pas donné ou script pas prêt)
      if (!window.adsbygoogle) {
        scheduleRetry();
        return;
      }

      // L'élément doit exister et avoir une largeur > 0 (sinon: No slot size / availableWidth=0)
      const el = insRef.current;
      const width = el?.offsetWidth ?? 0;

      if (!el || width === 0) {
        scheduleRetry();
        return;
      }

      // Évite double push
      if (pushedRef.current) return;

      try {
        pushedRef.current = true;
        window.adsbygoogle.push({});
      } catch (e) {
        // On retente plus tard, mais sans faire planter l'app
        pushedRef.current = false;
        console.warn("AdSense push failed:", e);
        scheduleRetry();
      }
    };

    const scheduleRetry = () => {
      if (cancelled) return;
      if (pushedRef.current) return;

      retriesRef.current += 1;
      if (retriesRef.current > maxRetries) return;

      setTimeout(tryPush, retryDelayMs);
    };

    // Laisse le layout se poser, puis tente
    const t = setTimeout(tryPush, retryDelayMs);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [client, slot, retryDelayMs, maxRetries]);

  // Si tu laisses des placeholders, on n'affiche rien
  if (!client || !slot || String(client).includes("XXXX")) return null;

  return (
    <div style={{ textAlign: "center", margin: "16px 0" }} className={className}>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", ...style }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? "true" : "false"}
      />
    </div>
  );
}
